import { Activity, ShieldCheck, ShieldX } from 'lucide-react';
import { useState, useRef } from 'react';

import { isoDateFromTimestamp } from '@/portainer/filters/filters';
import { Environment } from '@/react/portainer/environments/types';
import heartbeatup from '@/assets/ico/heartbeat-up.svg?c';
import heartbeatdown from '@/assets/ico/heartbeat-down.svg?c';

import { EnvironmentStatusBadgeItem } from './EnvironmentStatusBadgeItem';

interface Props {
  showLastCheckInDate?: boolean;
  environment: Environment;
  showMTLSStatus?: boolean;
}

export function EdgeIndicator({
  environment,
  showLastCheckInDate = false,
  showMTLSStatus = false,
}: Props) {
  const heartbeat = environment.Heartbeat;
  const MTLSStatus = environment.MTLSStatus ?? { enabled: false, ok: false };
  const [mtlsError, setMtlsError] = useState<string>('');
  const [showTooltip, setShowTooltip] = useState(false);
  const errorFetchedRef = useRef(false);

  async function fetchMTLSError(environmentId: number) {
    try {
      const response = await fetch(
        `/api/endpoints/${environmentId}/mtls_certificate_error`
      );
      if (response.ok) {
        const data = await response.json();
        setMtlsError(data.message || 'Unknown MTLS error');
      } else {
        setMtlsError('Failed to fetch MTLS error details');
      }
    } catch (error) {
      setMtlsError('Error retrieving MTLS status information');
    }
  }

  function handleMouseEnter() {
    setShowTooltip(true);
    // Only fetch the error if MTLS is enabled but not OK and we haven't fetched it yet
    if (
      MTLSStatus.enabled &&
      !MTLSStatus.ok &&
      environment.Id &&
      !errorFetchedRef.current
    ) {
      fetchMTLSError(environment.Id);
      errorFetchedRef.current = true;
    }
  }

  function getMTLSIcon() {
    if (MTLSStatus.ok) {
      return (
        <ShieldCheck
          className="icon icon-sm space-right !text-green-7"
          aria-hidden="true"
        />
      );
    }

    return (
      <ShieldX
        className="icon icon-sm space-right !text-error-7 cursor-pointer"
        aria-hidden="true"
      />
    );
  }

  const associated = !!environment.EdgeID;
  if (!associated) {
    return (
      <span role="status" aria-label="edge-status">
        <EnvironmentStatusBadgeItem aria-label="unassociated">
          <span className="whitespace-nowrap">Not associated</span>
        </EnvironmentStatusBadgeItem>
      </span>
    );
  }

  return (
    <span
      role="status"
      aria-label="edge-status"
      className="flex items-center gap-1"
    >
      <EnvironmentStatusBadgeItem
        color={heartbeat ? 'success' : 'danger'}
        icon={heartbeat ? heartbeatup : heartbeatdown}
        aria-label="edge-heartbeat"
      >
        heartbeat
      </EnvironmentStatusBadgeItem>
      {showMTLSStatus && MTLSStatus.enabled && (
        <div
          className="relative inline-flex"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <EnvironmentStatusBadgeItem
            color={MTLSStatus.ok ? 'success' : 'danger'}
            aria-label="edge-mtls"
          >
            {getMTLSIcon()}
            mTLS
          </EnvironmentStatusBadgeItem>
          {showTooltip && !MTLSStatus.ok && mtlsError && (
            <div
              className="absolute left-0 bottom-0 transform translate-y-full mt-2 z-50 bg-gray-900 text-white text-xs rounded p-2 shadow-lg pointer-events-none opacity-100"
              style={{
                minWidth: '200px',
                maxWidth: '300px',
                backgroundColor: 'rgba(17, 24, 39, 0.95)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              {mtlsError}
            </div>
          )}
        </div>
      )}
      {showLastCheckInDate && !!environment.LastCheckInDate && (
        <span
          className="small text-muted vertical-center"
          aria-label="edge-last-checkin"
          title="Last edge check-in"
        >
          <Activity className="icon icon-sm space-right" aria-hidden="true" />
          {isoDateFromTimestamp(environment.LastCheckInDate)}
        </span>
      )}
    </span>
  );
}
